import { useTranslation } from "react-i18next";
import {
  AppWindow,
  Gamepad2,
  GripVertical,
  Maximize2,
  Minimize2,
  MoveHorizontal,
  PanelRight,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { useGamesPortal } from "../hooks/use-games-portal";
import { useGamesPanelResize } from "../hooks/use-games-panel-resize";
import { useGamesPanelMove } from "../hooks/use-games-panel-move";
import { useCompactViewport } from "../hooks/use-compact-viewport";
import GamingSystem from "@/games/common/GamingSystem";
import { GamesPipCard } from "./GamesPipCard";

export const GamesDockPanel = () => {
  const { t } = useTranslation();
  const {
    isCollapsed,
    isFullscreen,
    selectedGame,
    view,
    panelWidth,
    sizeMode,
    panelSide,
    toggleCollapsed,
    closePortal,
    setSizeMode,
  } = useGamesPortal();

  const isMobile = useCompactViewport();
  const isSheet = isMobile && !isCollapsed;
  const resizeEnabled = !isCollapsed && !isFullscreen && !isMobile;
  const moveEnabled = !isCollapsed && !isFullscreen && !isMobile;
  const { isDragging, handleProps } = useGamesPanelResize(resizeEnabled);
  const {
    isMoving,
    hoverSide,
    previewLeft,
    handleProps: moveHandleProps,
  } = useGamesPanelMove(moveEnabled);

  const title =
    view === "playing" && selectedGame
      ? t(selectedGame.name)
      : t("games.menuTitle");

  const subtitle =
    view === "playing" && selectedGame
      ? t(selectedGame.description)
      : t("games.menuSubtitle");

  const dockedLeft = panelSide === "left";

  return (
    <>
      {isCollapsed && <GamesPipCard />}

      {isMoving && (
        <div className="pointer-events-none fixed inset-0 z-[1]">
          <div
            className={cn(
              "absolute top-20 bottom-4 left-4 w-[min(32vw,280px)] rounded-2xl border-2 border-dashed transition-colors",
              hoverSide === "left"
                ? "border-cyan-400/80 bg-cyan-400/10"
                : "border-white/15 bg-white/5 dark:border-white/10"
            )}
          />
          <div
            className={cn(
              "absolute top-20 bottom-4 right-4 w-[min(32vw,280px)] rounded-2xl border-2 border-dashed transition-colors",
              hoverSide === "right"
                ? "border-cyan-400/80 bg-cyan-400/10"
                : "border-white/15 bg-white/5 dark:border-white/10"
            )}
          />
        </div>
      )}

      <div
        className={cn(
          "fixed flex flex-col pointer-events-auto overflow-hidden border border-border-secondary bg-bg-primary text-text-primary shadow-2xl dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100",
          !isDragging && !isMoving && "transition-all duration-200",
          isCollapsed && "hidden",
          isMoving && "z-[2] opacity-90",
          isFullscreen || isSheet
            ? "inset-0 rounded-none"
            : cn(
                "top-20 bottom-4 max-w-[calc(100%-2rem)] rounded-2xl",
                !isMoving && (dockedLeft ? "left-4" : "right-4")
              )
        )}
        style={
          isCollapsed || isFullscreen || isMobile
            ? undefined
            : isMoving && previewLeft !== null
              ? { width: panelWidth, left: previewLeft, right: "auto" }
              : { width: panelWidth }
        }
        aria-hidden={isCollapsed}
      >
        {resizeEnabled && (
          <button
            type="button"
            {...handleProps}
            aria-label={t("games.resize")}
            title={t("games.resize")}
            className={cn(
              "absolute inset-y-0 z-20 hidden w-9 cursor-col-resize touch-none items-center justify-center md:flex",
              "focus-visible:outline-none",
              dockedLeft
                ? "right-0 translate-x-1/2"
                : "left-0 -translate-x-1/2"
            )}
          >
            <span
              aria-hidden
              className={cn(
                "absolute inset-y-0 left-1/2 w-px -translate-x-1/2 rounded-full transition-[width,background-color] duration-150",
                isDragging ? "w-0.5 bg-cyan-400" : "bg-transparent"
              )}
            />
            <span
              aria-hidden
              className={cn(
                "relative flex aspect-video h-4 items-center justify-center rounded-full p-0",
                "border border-cyan-400/30 bg-bg-primary text-cyan-600 shadow-sm",
                "dark:bg-gray-900 dark:text-cyan-300",
                isDragging && "border-cyan-400 bg-cyan-400 text-slate-950"
              )}
            >
              <MoveHorizontal className="size-3" />
            </span>
          </button>
        )}

        <div
          className={cn(
            "flex shrink-0 items-center justify-between gap-3 border-b border-border-secondary bg-bg-secondary px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800",
            isSheet && "pt-[max(0.65rem,env(safe-area-inset-top))]",
            moveEnabled && "cursor-grab active:cursor-grabbing"
          )}
          {...moveHandleProps}
          title={moveEnabled ? t("games.move") : undefined}
        >
          <div className="flex min-w-0 items-center gap-2">
            {moveEnabled && (
              <GripVertical className="hidden h-4 w-4 shrink-0 text-text-tertiary-600 md:block dark:text-gray-500" />
            )}
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-bg-tertiary dark:bg-gray-700">
              <Gamepad2 className="h-5 w-5 text-text-secondary dark:text-gray-200" />
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-text-primary dark:text-white">
                {title}
              </p>
              <p className="hidden truncate text-xs text-text-tertiary-600 sm:block dark:text-gray-400">
                {subtitle}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSizeMode("compact")}
              className={cn(
                "hidden h-8 w-8 md:inline-flex",
                sizeMode === "compact" &&
                  !isFullscreen &&
                  "bg-bg-tertiary dark:bg-gray-700"
              )}
              title={t("games.size.compact")}
            >
              <PanelRight className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSizeMode("comfort")}
              className={cn(
                "hidden h-8 w-8 md:inline-flex",
                sizeMode === "comfort" &&
                  !isFullscreen &&
                  "bg-bg-tertiary dark:bg-gray-700"
              )}
              title={t("games.size.comfort")}
            >
              <AppWindow className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => setSizeMode("full")}
              className={cn(
                "hidden h-8 w-8 md:inline-flex",
                isFullscreen && "bg-bg-tertiary dark:bg-gray-700"
              )}
              title={t("games.size.full")}
            >
              <Maximize2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={toggleCollapsed}
              className="h-8 w-8"
              title={t("games.collapse")}
            >
              <Minimize2 className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={closePortal}
              className="h-8 w-8"
              title={t("games.close")}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div
          className={cn(
            "min-h-0 flex-1",
            view === "playing" ? "overflow-hidden" : "overflow-y-auto"
          )}
        >
          <GamingSystem />
        </div>
      </div>
    </>
  );
};
